import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, PencilSimple, Trash, CreditCard, Money, DeviceMobile, Bank, QrCode, Wallet, Vault, User, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const TIPO_ICON = {
  efectivo: Money, debito: CreditCard, credito: CreditCard, qr: QrCode,
  transferencia: Bank, mercado_pago: DeviceMobile, cheque: Bank, otros: Wallet,
};
const TIPO_LABEL = {
  efectivo: "Efectivo", debito: "Tarjeta Débito", credito: "Tarjeta Crédito",
  qr: "QR", transferencia: "Transferencia", mercado_pago: "Mercado Pago",
  cheque: "Cheque", otros: "Otros",
};
const SCOPE_LABEL = { pos: "POS", ventas: "Ventas", ambos: "Ambos" };

const emptyForm = () => ({
  nombre: "", tipo: "efectivo", scope: "ambos",
  caja_default_id: "", activo: true, orden: 0,
  overrides: [],
});

export default function MediosPagoPage() {
  const [medios, setMedios] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [delTarget, setDelTarget] = useState(null);
  const [alivioCfg, setAlivioCfg] = useState({ caja_destino_id: "" });
  const [alivioSaving, setAlivioSaving] = useState(false);

  const load = async () => {
    const [{ data: m }, { data: c }, { data: u }, alivioResp] = await Promise.all([
      api.get("/medios-pago"),
      api.get("/cajas"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/pos/config/alivio-destino").catch(() => ({ data: null })),
    ]);
    setMedios(m || []);
    setCajas(c || []);
    setUsers(u || []);
    if (alivioResp?.data) setAlivioCfg({ caja_destino_id: alivioResp.data.caja_destino_id || "" });
  };
  useEffect(() => { load(); }, []);

  const cajasById = useMemo(() => Object.fromEntries((cajas || []).map((c) => [c.id, c])), [cajas]);
  const usersById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      nombre: m.nombre, tipo: m.tipo, scope: m.scope,
      caja_default_id: m.caja_default_id, activo: !!m.activo,
      orden: m.orden || 0,
      overrides: (m.overrides || []).map((o) => ({ user_id: o.user_id, caja_id: o.caja_id })),
    });
    setOpen(true);
  };

  const addOverride = () => {
    setForm({ ...form, overrides: [...form.overrides, { user_id: "", caja_id: "" }] });
  };
  const updateOverride = (idx, patch) => {
    const next = form.overrides.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    setForm({ ...form, overrides: next });
  };
  const removeOverride = (idx) => {
    setForm({ ...form, overrides: form.overrides.filter((_, i) => i !== idx) });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    if (!form.caja_default_id) return toast.error("Elegí una caja predeterminada");
    const payload = {
      nombre: form.nombre.trim(),
      tipo: form.tipo,
      scope: form.scope,
      caja_default_id: form.caja_default_id,
      activo: !!form.activo,
      orden: Number(form.orden) || 0,
      overrides: form.overrides.filter((o) => o.user_id && o.caja_id),
    };
    try {
      if (editingId) await api.put(`/medios-pago/${editingId}`, payload);
      else await api.post("/medios-pago", payload);
      toast.success(editingId ? "Medio actualizado" : "Medio creado");
      setOpen(false); setEditingId(null); setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/medios-pago/${delTarget.id}`);
      toast.success("Medio eliminado");
      setDelTarget(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const saveAlivioDestino = async () => {
    setAlivioSaving(true);
    try {
      await api.put("/pos/config/alivio-destino", {
        caja_destino_id: alivioCfg.caja_destino_id || null,
      });
      toast.success("Caja destino de alivios actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setAlivioSaving(false);
    }
  };

  const cajasEfectivo = useMemo(
    () => (cajas || []).filter((c) => (c.tipo || "").toLowerCase() === "efectivo"),
    [cajas]
  );

  return (
    <>
      <Topbar
        title="Medios de Pago"
        subtitle={`${medios.length} medio(s) configurados · POS y Ventas usan esta lista`}
        right={
          <Button
            data-testid="new-medio-pago-btn"
            onClick={openNew}
            className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
          >
            <Plus size={16} className="mr-1" /> Nuevo medio de pago
          </Button>
        }
      />

      <div className="p-4">
        {/* Panel de configuración: destino de los alivios de caja del POS */}
        <div
          className="border border-[#E5E5E5] bg-white rounded-sm p-4 mb-4"
          data-testid="alivio-destino-panel"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 font-heading font-semibold text-[14px]">
                <Vault size={16} className="text-[#6B1F24]" />
                Alivio de caja (POS)
              </div>
              <div className="text-[12px] text-[#666] mt-1 max-w-xl">
                Cuando el operador ejecuta un <b>Alivio de caja</b> desde el POS, el efectivo se
                transfiere desde <b>Efectivo Mostrador</b> hacia la caja seleccionada acá.
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1 min-w-[240px]">
                <Label className="text-[11px] text-[#666] uppercase tracking-wider font-mono">
                  Caja destino
                </Label>
                <Select
                  value={alivioCfg.caja_destino_id || "__none__"}
                  onValueChange={(v) => setAlivioCfg({ caja_destino_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger className="h-9 rounded-sm" data-testid="alivio-destino-select">
                    <SelectValue placeholder="(usar detección automática)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-neutral-500 italic">Detección automática</span>
                    </SelectItem>
                    {cajasEfectivo.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={saveAlivioDestino}
                disabled={alivioSaving}
                className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]"
                data-testid="alivio-destino-save"
              >
                {alivioSaving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
          {cajasEfectivo.length < 2 && (
            <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
              Necesitás al menos 2 cajas de tipo <b>Efectivo</b> (una origen del POS y otra destino).
              Crealas desde <b>Cajas y Bancos</b>.
            </div>
          )}
        </div>

        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-4 py-2 text-left w-14"></th>
                  <th className="px-4 py-2 text-left">Nombre</th>
                  <th className="px-4 py-2 text-left w-32">Tipo</th>
                  <th className="px-4 py-2 text-left w-24">Ámbito</th>
                  <th className="px-4 py-2 text-left">Caja predet.</th>
                  <th className="px-4 py-2 text-left w-40">Usuarios asignados</th>
                  <th className="px-4 py-2 text-center w-20">Activo</th>
                  <th className="px-4 py-2 text-right w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {medios.map((m) => {
                  const Icon = TIPO_ICON[m.tipo] || Wallet;
                  return (
                    <tr key={m.id} className="border-t border-neutral-100 hover:bg-neutral-50 group" data-testid={`medio-${m.id}`}>
                      <td className="px-4 py-2 text-center">
                        <Icon size={22} weight="duotone" className="inline text-[#6B1F24]" />
                      </td>
                      <td className="px-4 py-2 font-medium">{m.nombre}</td>
                      <td className="px-4 py-2 text-[#666]">{TIPO_LABEL[m.tipo] || m.tipo}</td>
                      <td className="px-4 py-2">
                        <span className="inline-block px-2 py-0.5 rounded-sm bg-[#F5F1E9] text-[#6B1F24] text-[11px] font-mono">
                          {SCOPE_LABEL[m.scope] || m.scope}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[#666] flex items-center gap-1.5">
                        <Vault size={14} className="text-neutral-400" />
                        {m.caja_default_nombre || "—"}
                      </td>
                      <td className="px-4 py-2">
                        {m.overrides?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {m.overrides.map((o, i) => (
                              <span key={i} className="text-[10px] font-mono bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded-sm">
                                {o.user_username} → {o.caja_nombre}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] font-mono text-neutral-400">Todos usan la caja predeterminada</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${m.activo ? "bg-emerald-500" : "bg-neutral-300"}`} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100">
                          <button data-testid={`edit-medio-${m.id}`} onClick={() => openEdit(m)}
                            className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]">
                            <PencilSimple size={14} />
                          </button>
                          <button data-testid={`del-medio-${m.id}`} onClick={() => setDelTarget(m)}
                            className="p-1.5 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600">
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {medios.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-[#999] text-[13px]">
                      Todavía no hay medios de pago configurados.<br />
                      Creá el primero con el botón "Nuevo medio de pago".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
        <SheetContent className="w-full sm:w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editingId ? "Editar medio de pago" : "Nuevo medio de pago"}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={save} className="space-y-4 mt-6">
            <div>
              <Label className="text-[12px]">Nombre</Label>
              <Input
                data-testid="medio-nombre"
                className="rounded-sm mt-1"
                required
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Efectivo, MP QR, Tarjeta Débito"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="medio-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPO_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Ámbito</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="medio-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pos">Solo POS</SelectItem>
                    <SelectItem value="ventas">Solo Ventas</SelectItem>
                    <SelectItem value="ambos">Ambos (POS y Ventas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[12px]">Caja predeterminada</Label>
              <Select value={form.caja_default_id} onValueChange={(v) => setForm({ ...form, caja_default_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="medio-caja">
                  <SelectValue placeholder="Elegí una caja…" />
                </SelectTrigger>
                <SelectContent>
                  {cajas.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre} <span className="text-[10px] text-neutral-400 font-mono ml-1">({c.tipo})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-[#999] mt-1 font-mono">
                Caja a la que se imputa el cobro por default. Podés cambiarla por usuario abajo.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Orden (menor = primero)</Label>
                <NumberInput
                  className="rounded-sm mt-1 font-mono"
                  value={form.orden}
                  onChange={(e) => setForm({ ...form, orden: e.target.value })}
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-[13px]">
                  <Switch
                    data-testid="medio-activo"
                    checked={form.activo}
                    onCheckedChange={(v) => setForm({ ...form, activo: v })}
                  />
                  Activo
                </label>
              </div>
            </div>

            {/* Overrides por usuario */}
            <div className="border border-[#E5E5E5] rounded-sm">
              <div className="px-3 py-2 bg-neutral-50 border-b flex items-center justify-between">
                <div>
                  <span className="text-[12px] font-semibold">Cajas por usuario</span>
                  <div className="text-[10px] text-[#999] font-mono">
                    Si un usuario cobra con este medio, la plata va a la caja indicada acá (en vez de la predeterminada).
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm text-[11px] h-7"
                  onClick={addOverride}
                  data-testid="medio-add-override"
                >
                  <Plus size={12} className="mr-1" /> Agregar
                </Button>
              </div>
              <div className="divide-y divide-neutral-100">
                {form.overrides.length === 0 && (
                  <div className="px-3 py-4 text-center text-[11px] font-mono text-[#999]">
                    Sin overrides. Todos los usuarios usan la caja predeterminada.
                  </div>
                )}
                {form.overrides.map((o, idx) => (
                  <div key={idx} className="p-2 flex items-center gap-2" data-testid={`override-${idx}`}>
                    <User size={16} className="text-neutral-400 shrink-0" />
                    <Select value={o.user_id} onValueChange={(v) => updateOverride(idx, { user_id: v })}>
                      <SelectTrigger className="rounded-sm h-8 flex-1"><SelectValue placeholder="Usuario…" /></SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.username}
                            {u.name && <span className="text-[10px] text-neutral-400 ml-1">({u.name})</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-neutral-300 text-xs">→</span>
                    <Select value={o.caja_id} onValueChange={(v) => updateOverride(idx, { caja_id: v })}>
                      <SelectTrigger className="rounded-sm h-8 flex-1"><SelectValue placeholder="Caja…" /></SelectTrigger>
                      <SelectContent>
                        {cajas.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => removeOverride(idx)}
                      className="p-1 text-neutral-400 hover:text-red-600"
                      data-testid={`override-remove-${idx}`}
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <Button
              data-testid="save-medio-btn"
              type="submit"
              className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
            >
              {editingId ? "Guardar cambios" : "Crear medio de pago"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar medio de pago</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar <span className="font-mono font-semibold">{delTarget?.nombre}</span>?
              Los cobros ya registrados no se ven afectados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-del-medio"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
